import React, { useEffect, useState, useRef } from "react";
import secureAxios from "../../../utils/secureAxios";
import { useAuth } from "../../../contexts/AuthContext";
import WidgetCard, { WidgetProgressBar } from "./WidgetCard";

const InvoiceSummaryWidget = ({ onlyMyData }) => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const hasFetchedRef = useRef(false);
  const lastParamsRef = useRef(null);

  useEffect(() => {
    const fetchSummary = async () => {
      if (!user?.user_id || !user?.tenant_id) {
        setLoading(false);
        return;
      }

      const paramsKey = `${user.user_id}-${user.tenant_id}-${onlyMyData}`;

      if (hasFetchedRef.current && lastParamsRef.current === paramsKey) {
        return;
      }

      hasFetchedRef.current = true;
      lastParamsRef.current = paramsKey;
      setLoading(true);

      try {
        const response = await secureAxios.get("/api/widgets/invoice-summary", {
          params: {
            only_my_data: onlyMyData,
          },
        });
        setData(response.data);
        setError(false);
      } catch (error) {
        if (process.env.NODE_ENV === "development") {
          console.error("Error fetching invoice summary", error);
        }
        setError(true);
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, [user?.user_id, user?.tenant_id, onlyMyData]);

  const formatCurrency = (value) => {
    const num = parseFloat(value) || 0;
    if (num >= 1000000) {
      return `$${(num / 1000000).toFixed(2)}M`;
    }
    if (num >= 1000) {
      return `$${(num / 1000).toFixed(1)}k`;
    }
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const currentYear = new Date().getFullYear();
  const lastYear = currentYear - 1;

  // Calculate growth percentage
  const calculateGrowth = () => {
    if (!data || !data.last_year_total || data.last_year_total === 0)
      return null;
    const growth =
      ((data.current_year_total - data.last_year_total) /
        data.last_year_total) *
      100;
    return growth;
  };

  const hasData =
    data &&
    (data.monthly_totals?.length > 0 ||
      data.current_year_total ||
      data.last_year_total);

  const headerRight = user?.is_admin ? (
    <small className="text-muted">
      <i className={`bi bi-${onlyMyData ? "person" : "people"}-fill`}></i>
    </small>
  ) : null;

  const growth = data ? calculateGrowth() : null;

  return (
    <WidgetCard
      title="Invoice Summary"
      subtitle={onlyMyData ? "My invoices" : "All invoices"}
      icon="bi-receipt"
      iconColor="purple"
      loading={loading}
      error={error}
      errorMessage="Unable to load data"
      empty={!hasData}
      emptyMessage="No invoice data available"
      headerRight={headerRight}
    >
      {hasData && (
        <>
          {/* Year Summary Cards */}
          <div className="row g-2 mb-3">
            <div className="col-6">
              <div className="widget-year-card text-center">
                <div className="small text-muted mb-1">{currentYear}</div>
                <div className="fw-bold text-purple fs-5">
                  {formatCurrency(data.current_year_total)}
                </div>
                {growth !== null && (
                  <div className="mt-1">
                    <span
                      className={`widget-growth-badge ${
                        growth >= 0
                          ? "widget-growth-badge--positive"
                          : "widget-growth-badge--negative"
                      }`}
                    >
                      <i
                        className={`bi bi-arrow-${
                          growth >= 0 ? "up" : "down"
                        }-short`}
                      ></i>
                      {Math.abs(growth).toFixed(0)}%
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="col-6">
              <div className="widget-year-card text-center">
                <div className="small text-muted mb-1">{lastYear}</div>
                <div className="fw-bold fs-5">
                  {formatCurrency(data.last_year_total)}
                </div>
                <div className="mt-1">
                  <span className="small text-muted">Previous year</span>
                </div>
              </div>
            </div>
          </div>

          {/* Monthly Breakdown */}
          {data.monthly_totals?.length > 0 && (
            <div className="border-top pt-3">
              <h6 className="text-muted small mb-2">
                {currentYear} Monthly Breakdown
              </h6>
              <div className="monthly-list">
                {data.monthly_totals.map((row) => (
                  <div
                    key={`month-${row.month_num}`}
                    className="widget-month-row d-flex align-items-center"
                  >
                    <div className="widget-month-indicator"></div>
                    <div className="flex-grow-1 d-flex justify-content-between align-items-center">
                      <span className="fw-medium">
                        {row.month_name?.trim()}
                      </span>
                      <span className="fw-semibold text-purple">
                        {formatCurrency(row.total)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Visual Progress Bar */}
          {data.current_year_total > 0 && data.last_year_total > 0 && (
            <div className="widget-footer">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <small className="text-muted">YoY Comparison</small>
                <small className="text-muted">
                  {(
                    (data.current_year_total /
                      Math.max(data.current_year_total, data.last_year_total)) *
                    100
                  ).toFixed(0)}
                  % of peak
                </small>
              </div>
              <WidgetProgressBar
                height="6px"
                segments={[
                  {
                    color: "purple",
                    percent:
                      (data.current_year_total /
                        Math.max(
                          data.current_year_total,
                          data.last_year_total
                        )) *
                      100,
                  },
                ]}
              />
            </div>
          )}
        </>
      )}
    </WidgetCard>
  );
};

export default InvoiceSummaryWidget;
